export const statuses = [
  "todo",
  "in_progress",
  "in_review",
  "done",
  "cancelled",
] as const;
export type Status = (typeof statuses)[number];
export const statusNames: Record<Status, string> = {
  todo: "待处理",
  in_progress: "开发中",
  in_review: "测试中",
  done: "已完成",
  cancelled: "已取消",
};
export const defaultIssueTypes = ["Task", "Bug", "Feature"] as const;
export interface TaskOptions {
  labels: string[];
  issueTypes: { name: string; description?: string; color?: string }[];
  typeMode: "local" | "github" | "unavailable";
  warning?: string;
}
export type Priority = "urgent" | "high" | "medium" | "low";
export type ProjectRole = "owner" | "member" | "viewer";
export const disciplines = ["development", "ui", "testing"] as const;
export type Discipline = (typeof disciplines)[number];
export const disciplineNames: Record<Discipline, string> = {
  development: "开发",
  ui: "UI",
  testing: "测试",
};
export const aiProviders = ["openai", "deepseek", "compatible"] as const;
export type AiProvider = string;
export const aiActions = ["summary", "plan", "ask"] as const;
export type AiAction = (typeof aiActions)[number];
export interface AiProviderInfo {
  id: AiProvider;
  name: string;
  model: string;
  configured: boolean;
  available: boolean;
  vision?: boolean;
  imageModel?: string;
}
export interface AiConfiguration {
  providers: AiProviderInfo[];
  defaultProvider: AiProvider | null;
  demo: boolean;
  userHourlyLimit: number;
  projectDailyLimit: number;
}
export interface AiTaskDraft {
  id: string;
  title: string;
  description: string;
  priority: Priority;
  labels: string[];
  issue_type?: string | null;
  assignee_id?: string | null;
  assignee_ids?: string[];
  due_date?: string | null;
}
export interface AiResult {
  answer: string;
  tasks: AiTaskDraft[];
  followups: string[];
}
export interface AiRun {
  id: string;
  project_id: string;
  action: AiAction;
  prompt: string;
  provider: AiProvider;
  model: string;
  status: "queued" | "running" | "completed" | "failed" | "cancelled";
  result: AiResult | null;
  error: string | null;
  demo: boolean;
  created_at: string;
  context_info: {
    total_tasks: number;
    included_tasks: number;
    truncated: boolean;
  } | null;
  usage: { input_tokens: number | null; output_tokens: number | null } | null;
  applied: { draft_id: string; task_id: string; title: string }[];
}
export interface User {
  id: string;
  name: string;
  telegram_id: string | null;
  username: string | null;
  github_id: string | null;
  github_login: string | null;
}
export interface Member extends User {
  role: ProjectRole;
  disciplines: Discipline[];
  source: "manual" | "github";
  github_permission: string | null;
  github_teams: { id: string; name: string; slug: string }[];
}
export interface Project {
  id: string;
  name: string;
  description: string;
  color: string;
  role: ProjectRole;
  repo_id: string | null;
  repo_owner: string | null;
  repo_name: string | null;
  installation_id: string | null;
  connection_status: string;
  chat_id: string | null;
  chat_title: string | null;
  timezone: string;
  reminders: boolean;
  daily_digest: boolean;
  group_updates: boolean;
  direct_notifications: boolean;
  auto_testing: boolean;
  sync_source: "manual" | "github";
  members_sync_enabled: boolean;
  members_synced_at: string | null;
  sync_error: string | null;
  team_sync_warning: string | null;
  repo_archived: boolean;
  ai_enabled: boolean;
  ai_provider: AiProvider | null;
}
export interface ProjectCatalogEntry extends Project {
  github_account_login: string | null;
  github_account_type: string | null;
  in_workspace: boolean;
  favorite: boolean;
  last_opened_at: string | null;
}
export interface PullRequest {
  id: string;
  number: number;
  title: string;
  url: string;
  state: "draft" | "open" | "merged" | "closed";
  author: string;
  checks: string | null;
}
export interface Task {
  revision: number;
  id: string;
  number: number;
  project_id: string;
  title: string;
  description: string;
  status: Status;
  priority: Priority;
  assignee_id: string | null;
  assignees: User[];
  testers: User[];
  testing_pr_id: string | null;
  github_state: "open" | "closed" | null;
  due_date: string | null;
  labels: string[];
  issue_type: string | null;
  issue_type_source: "local" | "github";
  github_issue_number: number | null;
  github_url: string | null;
  sync_state: "local" | "pending" | "synced" | "error";
  created_at: string;
  updated_at: string;
  pull_requests: PullRequest[];
}
export interface Activity {
  id: string;
  project_id: string;
  task_id: string | null;
  kind: string;
  title: string;
  detail: string;
  actor: string;
  created_at: string;
  url: string | null;
}
export interface ProjectDocumentSummary {
  id: string;
  project_id: string;
  title: string;
  important: boolean;
  excerpt: string;
  revision: number;
  created_by: string | null;
  updated_by: string | null;
  updated_by_name: string | null;
  created_at: string;
  updated_at: string;
  file_count: number;
}
export interface ProjectDocumentFile {
  id: string;
  document_id: string;
  name: string;
  media_type: string;
  size: number;
  created_at: string;
  url: string;
}
export interface ProjectDocument extends ProjectDocumentSummary {
  content: string;
  files: ProjectDocumentFile[];
}
export interface ProjectDocumentsPage {
  documents: ProjectDocumentSummary[];
  pagination: { page: number; page_size: number; total: number; pages: number };
}
export interface DocumentShare {
  url: string;
  created_at: string;
}
export type SharedDocumentFile = Omit<ProjectDocumentFile, "document_id">;
export interface SharedProjectDocument {
  title: string;
  content: string;
  updated_at: string;
  files: SharedDocumentFile[];
}
export interface JobView {
  id: string;
  kind: string;
  status: string;
  attempts: number;
  last_error: string | null;
  created_at: string;
}
export interface Bootstrap {
  mode: "demo" | "live";
  user: User;
  projects: ProjectCatalogEntry[];
  githubConfigured: boolean;
  telegramConfigured: boolean;
  telegramBotUrl?: string | null;
  githubInstallUrl: string | null;
  githubSyncJobs: JobView[];
  ai: AiConfiguration;
}
export interface ProjectData {
  tasks: TaskSummary[];
  assignee_options: User[];
  members: Member[];
  activities: Activity[];
  jobs: JobView[];
  pagination: {
    page: number;
    page_size: number;
    total: number;
    pages: number;
    status_counts: Record<Status, number>;
  };
  summary: {
    total: number;
    done: number;
    mine: number;
    review: number;
    issue_types: string[];
  };
}
export type TaskSummary = Omit<Task, "description"> & { description?: string };
export interface RepositoryChoice {
  id: string;
  installation_id: string;
  owner: string;
  name: string;
  private: boolean;
}
